// API Response types for Applications and Trunks

import type { RPlivoApplication } from 'v2/types/api/settings'
import type { SipPlatformKey } from './platform-config'

export interface RTrunkPrimaryUri {
  id: number
  is_auth: boolean
  name: string
  uri: string
  uuid: string
}

export interface RTrunkNumber {
  id: number
  number: string
}

export interface RTrunk {
  account_id: number
  enabled: boolean
  fallback_uri: RTrunkPrimaryUri | null
  id: number
  name: string
  numbers: RTrunkNumber[] | null
  primary_uri: RTrunkPrimaryUri
  secure: boolean
  uuid: string
  // For outbound trunks (Plivo API format)
  trunk_id?: string
  trunk_domain?: string
  trunk_status?: 'enabled' | 'disabled'
  trunk_direction?: 'inbound' | 'outbound'
  ipacl_uuid?: string | null
  credential_uuid?: string | null
  message_url?: string
  message_method?: 'GET' | 'POST'
}

export interface RTrunks {
  api_id?: string
  meta: {
    limit: number
    offset: number
    total_count: number
    previous: string | null
    next: string | null
  }
  objects: RTrunk[]
}

// Combined type for unified table display
export enum ApplicationType {
  XML = 'xml',
  SIP = 'sip',
}

export interface ApplicationTableItem {
  id: string
  name: string
  type: 'XML' | 'inbound' | 'outbound'
  // Original data for editing
  originalData: RPlivoApplication | RTrunk
}

// Shared XML Application fields
export interface XmlApplicationFields {
  answer_url?: string
  answer_method?: 'GET' | 'POST'
  hangup_url?: string
  hangup_method?: 'GET' | 'POST'
  message_url?: string
  message_method?: 'GET' | 'POST'
  fallback_answer_url?: string
  fallback_method?: 'GET' | 'POST'
  default_number_app?: boolean
  default_endpoint_app?: boolean
  log_incoming_messages?: boolean
  subaccount?: string
  public_uri?: boolean
}

// Base interfaces for common fields
interface BaseTrunkFields {
  name: string
  trunk_domain?: string
  secure?: boolean
}

interface BaseInboundTrunkFields extends BaseTrunkFields {
  primary_uri: number
  fallback_uri?: number
  enabled?: boolean
  linked_numbers?: string[]
}

interface BaseOutboundTrunkFields extends BaseTrunkFields {
  ipacl_uuid?: string
  credential_uuid?: string
  trunk_status?: 'enabled' | 'disabled'
  trunk_authentication_type?: 'ip_access_control_list' | 'credentials' | 'both' // UI-only field
}

// Form types
export interface CreateApplicationFormValues extends XmlApplicationFields {
  app_name: string
}

// Base unified form values
interface BaseUnifiedApplicationFormValues extends XmlApplicationFields {
  name: string
  linked_numbers?: string[]
  trunk_direction?: 'inbound' | 'outbound' // Present when application_type === 'sip', undefined for XML
  trunk_domain?: string // Present when application_type === 'sip', undefined for XML
  // UI-only: drives platform templating/filtering, never sent to the API
  sip_platform?: SipPlatformKey
  // UI-only (LiveKit outbound, per design): the project subdomain, collected to
  // verify the user is really on LiveKit. The outbound trunk API takes no URI.
  outbound_uri?: string
}

// XML Application form values
export interface XmlApplicationFormValues extends BaseUnifiedApplicationFormValues {
  application_type: ApplicationType.XML
  app_name: string
}

// Inbound SIP Trunk form values
export interface InboundSipTrunkFormValues
  extends BaseUnifiedApplicationFormValues,
    BaseInboundTrunkFields {
  application_type: ApplicationType.SIP
  trunk_direction: 'inbound'
  message_url?: string
  message_method?: 'GET' | 'POST'
}

// Outbound SIP Trunk form values
export interface OutboundSipTrunkFormValues
  extends BaseUnifiedApplicationFormValues,
    BaseOutboundTrunkFields {
  application_type: ApplicationType.SIP
  trunk_direction: 'outbound'
}

// Discriminated union for unified form values
export type UnifiedApplicationFormValues =
  | XmlApplicationFormValues
  | InboundSipTrunkFormValues
  | OutboundSipTrunkFormValues

// Type helper to narrow UnifiedApplicationFormValues when we know it's an inbound trunk
export type InboundTrunkFormValues = InboundSipTrunkFormValues

// Legacy type for backward compatibility - all possible trunk fields
export interface CreateTrunkFormValues {
  name: string
  trunk_direction?: 'inbound' | 'outbound'
  trunk_domain?: string
  secure?: boolean
  ipacl_uuid?: string
  credential_uuid?: string
  primary_uri?: number
  fallback_uri?: number
  trunk_status?: 'enabled' | 'disabled'
  enabled?: boolean
}

// Payload interfaces for API requests - derived from form values with required fields
export interface CreateInboundTrunkPayload
  extends Pick<
    InboundSipTrunkFormValues,
    'name' | 'primary_uri' | 'fallback_uri' | 'message_url' | 'message_method' | 'linked_numbers'
  > {
  enabled: boolean // Required in payload
}

export interface UpdateInboundTrunkPayload
  extends Pick<
    InboundSipTrunkFormValues,
    'name' | 'primary_uri' | 'fallback_uri' | 'message_url' | 'message_method' | 'linked_numbers'
  > {
  enabled: boolean // Required in payload
}

export interface UpdateApplicationPayload {
  app_id: string
  payload: Partial<CreateApplicationFormValues>
}

export interface DeleteApplicationPayload {
  app_id: string
  cascade?: boolean
  new_endpoint_application?: string
}

export interface UpdateTrunkPayload {
  trunk_id: string
  payload: Partial<CreateTrunkFormValues>
}
export interface DeleteTrunkPayload {
  trunk_id: string
}
